import { Breadcrumb } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import { Link, useLocation } from "react-router-dom";
import { useBreadcrumb } from "../context/BreadcrumbContext";
import { locateNav, modulePath } from "../helpers/nav";

interface Crumb {
  label: string;
  path?: string;
}

/**
 * The trail that replaces the old sidebar: Home → module → page → document.
 * Derived from the route via the nav registry; the last crumb on a detail route is the
 * page's own title, which PageHeader registers on the breadcrumb context.
 */
export function Breadcrumbs() {
  const location = useLocation();
  const { leaf } = useBreadcrumb();
  const { module, item } = locateNav(location.pathname);

  const crumbs: Crumb[] = [];
  if (module) {
    crumbs.push({ label: module.label, path: modulePath(module) });
    if (item) crumbs.push({ label: item.label, path: item.path });
  }

  // A route deeper than the page it belongs to (a detail view) gets one more crumb,
  // named after whatever the page put in its header.
  const owner = item?.path ?? module?.path;
  const atRoot = location.pathname === "/"; // the home icon already says where we are
  if (leaf && !atRoot && (!owner || location.pathname !== owner) && crumbs[crumbs.length - 1]?.label !== leaf) {
    crumbs.push({ label: leaf });
  }

  // The last crumb is where we already are — never a link.
  const last = crumbs.length - 1;

  return (
    <Breadcrumb
      className="glut-breadcrumbs"
      // Named, because it is not the only navigation landmark on the page: the recent-pages
      // list is another, and an unlabelled pair is ambiguous to a screen reader and to
      // anything else addressing the page by role.
      aria-label="Breadcrumb"
      items={[
        {
          title: (
            <Link to="/" className="glut-breadcrumb-home">
              <HomeOutlined />
              Home
            </Link>
          ),
        },
        ...crumbs.map((crumb, i) => ({
          title: crumb.path && i !== last ? <Link to={crumb.path}>{crumb.label}</Link> : crumb.label,
        })),
      ]}
    />
  );
}
